import React, { useState, useRef } from 'react';
import axios from 'axios';

const TravelDocumentsTab = ({ formData, handleNestedInputChange, executiveId, onPassportsChange }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [editingPassportId, setEditingPassportId] = useState(null);
  const [viewingPassportId, setViewingPassportId] = useState(null);
  const [loadingImageId, setLoadingImageId] = useState(null);
  const fileInputRef = useRef(null);

  const passports = formData.passports || [];

  const getPassportImageUrl = async (imageKey) => {
    const apiBase = process.env.REACT_APP_API_BASE || '/api';

    const headers = {};

    const response = await axios.get(
      `${apiBase}/passport-image/${encodeURIComponent(imageKey)}`,
      { headers }
    );
    return response.data.url;
  };

  const handleViewImage = async (passport) => {
    if (!passport.image_key) return;

    setLoadingImageId(passport.id);
    try {
      const url = await getPassportImageUrl(passport.image_key);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error getting passport image URL:', error);
      setUploadError('Failed to load passport image');
    } finally {
      setLoadingImageId(null);
    }
  };

  const handleDownloadImage = async (passport) => {
    if (!passport.image_key) return;

    setLoadingImageId(passport.id);
    try {
      const url = await getPassportImageUrl(passport.image_key);

      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `passport_${passport.last_name}_${passport.first_name}.${passport.image_key.split('.').pop() || 'jpg'}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading passport image:', error);
      setUploadError('Failed to download passport image');
    } finally {
      setLoadingImageId(null);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please upload a valid image (JPEG, PNG, WebP) or PDF file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const apiBase = process.env.REACT_APP_API_BASE || '/api';

      const headers = {};

      // Upload and parse passport
      const response = await axios.post(
        `${apiBase}/parse-passport`,
        formDataUpload,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );

      const parsedData = response.data;

      // Create new passport entry
      const newPassport = {
        id: `passport-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        first_name: parsedData.first_name || '',
        last_name: parsedData.last_name || '',
        passport_number: parsedData.passport_number || '',
        date_of_birth: parsedData.date_of_birth || '',
        place_of_birth: parsedData.place_of_birth || '',
        date_of_issue: parsedData.date_of_issue || '',
        date_of_expiration: parsedData.date_of_expiration || '',
        authority: parsedData.authority || '',
        nationality: parsedData.nationality || '',
        image_key: parsedData.image_key || null,
        created_at: new Date().toISOString()
      };

      // Add to passports array
      const updatedPassports = [...passports, newPassport];
      onPassportsChange(updatedPassports);

      // Open edit mode to allow corrections
      setEditingPassportId(newPassport.id);

    } catch (error) {
      console.error('Error uploading passport:', error);
      setUploadError(error.response?.data?.detail || 'Failed to parse passport. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUpdatePassport = (passportId, field, value) => {
    const updatedPassports = passports.map(p =>
      p.id === passportId ? { ...p, [field]: value } : p
    );
    onPassportsChange(updatedPassports);
  };

  const handleDeletePassport = (passportId) => {
    const updatedPassports = passports.filter(p => p.id !== passportId);
    onPassportsChange(updatedPassports);
    if (viewingPassportId === passportId) setViewingPassportId(null);
    if (editingPassportId === passportId) setEditingPassportId(null);
  };

  const handleAddManualPassport = () => {
    const newPassport = {
      id: `passport-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      first_name: '',
      last_name: '',
      passport_number: '',
      date_of_birth: '',
      place_of_birth: '',
      date_of_issue: '',
      date_of_expiration: '',
      authority: '',
      nationality: '',
      image_key: null,
      created_at: new Date().toISOString()
    };

    const updatedPassports = [...passports, newPassport];
    onPassportsChange(updatedPassports);

    // Open edit mode for the new passport
    setEditingPassportId(newPassport.id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    try {
      return new Date(dateStr) < new Date();
    } catch {
      return false;
    }
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    try {
      const expiryDate = new Date(dateStr);
      const sixMonthsFromNow = new Date();
      sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
      return expiryDate > new Date() && expiryDate < sixMonthsFromNow;
    } catch {
      return false;
    }
  };

  return (
    <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl shadow-slate-200/50 rounded-3xl p-8 overflow-hidden relative">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50/40 via-transparent to-emerald-50/40 pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-center gap-4 mb-8">
        <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Travel Documents</h2>
          <p className="text-slate-500 mt-1">Upload and manage passport information</p>
        </div>
      </div>

      <div className="relative space-y-6">
        {/* Upload Section */}
        <div className="border-2 border-dashed border-green-300 rounded-xl p-6 bg-green-50/50">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
          />

          <div className="text-center">
            <svg className="w-12 h-12 text-green-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Upload Passport Image</h3>
            <p className="text-sm text-slate-500 mb-4">
              Upload a photo or scan of your passport. We'll automatically extract the information.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Parsing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Upload Passport
                  </>
                )}
              </button>
              <span className="text-slate-400 text-sm">or</span>
              <button
                type="button"
                onClick={handleAddManualPassport}
                disabled={isUploading}
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-green-600 text-green-700 hover:bg-green-50 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Add Manually
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-3">Upload supports JPEG, PNG, WebP, and PDF (max 10MB)</p>
          </div>

          {uploadError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-red-700">{uploadError}</span>
              <button
                type="button"
                onClick={() => setUploadError(null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Passports List */}
        {passports.length > 0 ? (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-700">Saved Passports ({passports.length})</h3>

            {passports.map((passport) => (
              <div
                key={passport.id}
                className={`border rounded-xl overflow-hidden transition-all ${
                  isExpired(passport.date_of_expiration)
                    ? 'border-red-300 bg-red-50/50'
                    : isExpiringSoon(passport.date_of_expiration)
                    ? 'border-amber-300 bg-amber-50/50'
                    : 'border-slate-200 bg-white'
                }`}
              >
                {/* Passport Header */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">
                        {passport.first_name} {passport.last_name}
                      </div>
                      <div className="text-sm text-slate-500">
                        {passport.nationality} - {passport.passport_number}
                      </div>
                    </div>
                    {isExpired(passport.date_of_expiration) && (
                      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                        Expired
                      </span>
                    )}
                    {isExpiringSoon(passport.date_of_expiration) && !isExpired(passport.date_of_expiration) && (
                      <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                        Expires Soon
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* View/Download Image buttons - only show if image_key exists */}
                    {passport.image_key && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleViewImage(passport)}
                          disabled={loadingImageId === passport.id}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                          title="View passport image"
                        >
                          {loadingImageId === passport.id ? (
                            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadImage(passport)}
                          disabled={loadingImageId === passport.id}
                          className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Download passport image"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => setViewingPassportId(viewingPassportId === passport.id ? null : passport.id)}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View details"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingPassportId(editingPassportId === passport.id ? null : passport.id)}
                      className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeletePassport(passport.id)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* View Mode */}
                {viewingPassportId === passport.id && editingPassportId !== passport.id && (
                  <div className="px-4 pb-4 border-t border-slate-100">
                    <div className="grid md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Date of Birth</span>
                        <div className="font-medium text-slate-800">{formatDate(passport.date_of_birth) || '-'}</div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Place of Birth</span>
                        <div className="font-medium text-slate-800">{passport.place_of_birth || '-'}</div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Nationality</span>
                        <div className="font-medium text-slate-800">{passport.nationality || '-'}</div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Date of Issue</span>
                        <div className="font-medium text-slate-800">{formatDate(passport.date_of_issue) || '-'}</div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Date of Expiration</span>
                        <div className={`font-medium ${isExpired(passport.date_of_expiration) ? 'text-red-600' : isExpiringSoon(passport.date_of_expiration) ? 'text-amber-600' : 'text-slate-800'}`}>
                          {formatDate(passport.date_of_expiration) || '-'}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Issuing Authority</span>
                        <div className="font-medium text-slate-800">{passport.authority || '-'}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Mode */}
                {editingPassportId === passport.id && (
                  <div className="px-4 pb-4 border-t border-slate-100">
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">First Name</label>
                        <input
                          type="text"
                          value={passport.first_name || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'first_name', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">Last Name</label>
                        <input
                          type="text"
                          value={passport.last_name || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'last_name', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">Passport Number</label>
                        <input
                          type="text"
                          value={passport.passport_number || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'passport_number', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">Nationality</label>
                        <input
                          type="text"
                          value={passport.nationality || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'nationality', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">Date of Birth</label>
                        <input
                          type="date"
                          value={passport.date_of_birth || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'date_of_birth', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">Place of Birth</label>
                        <input
                          type="text"
                          value={passport.place_of_birth || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'place_of_birth', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">Date of Issue</label>
                        <input
                          type="date"
                          value={passport.date_of_issue || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'date_of_issue', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">Date of Expiration</label>
                        <input
                          type="date"
                          value={passport.date_of_expiration || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'date_of_expiration', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <label className="text-xs font-medium text-slate-600">Issuing Authority (Country)</label>
                        <input
                          type="text"
                          value={passport.authority || ''}
                          onChange={(e) => handleUpdatePassport(passport.id, 'authority', e.target.value)}
                          className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-green-400 focus:ring-green-400 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button
                        type="button"
                        onClick={() => setEditingPassportId(null)}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
            </svg>
            <p>No passports uploaded yet</p>
            <p className="text-sm text-slate-400 mt-1">Upload a passport image to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelDocumentsTab;
